import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Panel } from "@/components/common";
import { Scale } from "lucide-react";

const TONE = {
  matched: "text-emerald-400", quantity_mismatch: "text-rose-400", price_mismatch: "text-amber-300",
  missing_in_app: "text-rose-400", missing_at_broker: "text-amber-300", unreadable: "text-rose-400",
};

const LABEL = {
  matched: "Matched", quantity_mismatch: "Quantity differs", price_mismatch: "Avg price differs",
  missing_in_app: "Only at broker", missing_at_broker: "Only in Tradify", unreadable: "Unreadable",
};

function Row({ r }) {
  return (
    <tr data-testid={`recon-row-${r.symbol}`} className="border-t border-[var(--border-subtle)]">
      <td className="py-2 pr-3 font-mono text-slate-200">{r.symbol}</td>
      <td className={`py-2 pr-3 ${TONE[r.status]}`} data-testid={`recon-status-${r.symbol}`}>{LABEL[r.status]}</td>
      <td className="py-2 pr-3 font-mono text-right">{r.broker_qty ?? "—"}</td>
      <td className="py-2 pr-3 font-mono text-right">{r.app_qty ?? "—"}</td>
      <td className="py-2 pr-3 font-mono text-right">{r.qty_difference ?? "—"}</td>
      <td className="py-2 pr-3 font-mono text-right">{r.broker_avg_price ?? "—"}</td>
      <td className="py-2 font-mono text-right">{r.app_avg_price ?? "—"}</td>
    </tr>
  );
}

export default function HoldingsReconciliation() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["recon-holdings"],
    queryFn: async () => (await api.get("/reconciliation/holdings")).data,
  });
  return (
    <div className="space-y-4" data-testid="holdings-reconciliation-page">
      <div>
        <h1 className="text-2xl font-semibold text-slate-100">Holdings Reconciliation</h1>
        <p className="text-sm text-slate-400 mt-1">Sharekhan holdings compared with Tradify AI records. Read-only; nothing is corrected automatically.</p>
      </div>
      <div data-testid="recon-mode-banner" className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
        SIMULATED — broker side uses labelled synthetic Sharekhan data. No broker connection is made.
      </div>
      <Panel title="Holdings comparison" testId="recon-panel" actions={<Scale className="w-4 h-4 text-slate-500" />}>
        <div className="p-4 overflow-x-auto text-xs">
          {isLoading && <div data-testid="recon-loading" className="text-slate-500">Loading…</div>}
          {isError && <div data-testid="recon-error" className="text-rose-400">Could not load reconciliation.</div>}
          {data && (
            <>
              <div data-testid="recon-summary" className="mb-3 text-slate-400">
                {data.summary.total} symbols · {data.summary.matched || 0} matched · {data.summary.total - (data.summary.matched || 0)} need review
              </div>
              <table className="w-full min-w-[640px]">
                <thead className="text-slate-500 text-left">
                  <tr><th className="pb-2">Symbol</th><th>Status</th><th className="text-right">Broker qty</th><th className="text-right">Tradify qty</th>
                    <th className="text-right">Difference</th><th className="text-right">Broker avg</th><th className="text-right">Tradify avg</th></tr>
                </thead>
                <tbody>{data.rows.map((r) => <Row key={r.symbol} r={r} />)}</tbody>
              </table>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
